/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';

import { Hint } from 'components/shared/Hint';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReactComponent as ArrowDownSvg } from 'assets/Icons/speaker-arrow-down.svg';
import { ReactComponent as AddSvg } from 'assets/Icons/transcript-add.svg';
import { ReactComponent as MergeSvg } from 'assets/Icons/transcript-merge.svg';
import { ReactComponent as SplitSvg } from 'assets/Icons/Auto Split Caption.svg';
import { ReactComponent as DeleteSvg } from 'assets/Icons/transcript-remove.svg';
import { Ripple } from '@rmwc/ripple';

import {
  highlightNodeHoverCss,
  iconStyles,
  timecodeEditable,
  transcriptButtonCss,
} from 'components/shared/twin.styles';
import { isDevMode, isPublicShared, secToTimeCode } from '../../MediaUtilities';
import { ReactComponent as Play } from 'assets/Icons/Play.svg';
import { ReactComponent as Tplay } from 'assets/Icons/playback-pause.svg';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import {
  Editor,
  Node,
  NodeEntry,
  Path,
  RemoveNodeOperation,
  Transforms,
} from 'slate';
import { ReactEditor } from 'slate-react';
import { first, isArray, isEmpty, isNil, last } from 'lodash';
import { useClickAway, useToggle, useUpdate } from 'react-use';
import { CustomElement } from 'types/slate-custom-types';
import { formatSpeaker } from 'utils/transcript.util';
import { breakCaption, handleAddTranscript } from 'utils/caption.util';
import { SpeakerDropdown } from './SpeakerDropdown';
import { customToast } from 'utils/toast.util';
import {
  resolveTranscriptScrollRoot,
  runStructuralTranscriptEdit,
  transcriptVisibility,
  transcriptVisibilityKey,
} from './transcriptVisibility';
import {
  flushPlaybackHighlight,
  isPlaybackActiveCaption,
  startTranscriptLinePlayback,
} from './playbackHighlight';
import { transcriptChunkStore } from './TranscriptChunkStore';
import { logTranscriptLinePlayDiagnosis } from './chunkDebugLog';

type TranscriptProps = {
  attributes: any;
  element: CustomElement;
  children: any;
  onChangeTimecode?: (params: any) => unknown;
};

export const Transcript = React.memo(function TranscriptRow({
  attributes,
  element,
  children,
  onChangeTimecode,
}: TranscriptProps) {
  const toggle = useSelector((state: RootState) => state.toggle);
  const followTranscript = toggle.isFollowing && !toggle.isEditMode;
  const isReadOnly = ReactEditor.isReadOnly(window.Editor);

  const update = useUpdate();
  const shellRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  const visibilityKey = transcriptVisibilityKey(element?.data?.start);
  const [mountHeavy, setMountHeavy] = useState(() =>
    transcriptVisibility.isHeavyMounted(visibilityKey),
  );

  useEffect(() => {
    const root = resolveTranscriptScrollRoot(shellRef.current);
    if (root) {
      transcriptVisibility.setScrollRoot(root);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const sync = () => {
      if (!mountedRef.current) return;
      setMountHeavy(transcriptVisibility.isHeavyMounted(visibilityKey));
    };
    sync();
    return transcriptVisibility.subscribe(visibilityKey, sync);
  }, [visibilityKey]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    return transcriptVisibility.observe(el, visibilityKey);
  }, [visibilityKey]);

  const handleMergeTranscript = () => {
    const t0 = performance.now();
    try {
      runStructuralTranscriptEdit(() => {
        const path = ReactEditor.findPath(window.Editor, element);
        const node: CustomElement = Node.get(window.Editor, path);

        const prevEntry: any = Editor.previous(window.Editor, { at: path });
        const newEnd = node?.data?.end;

        if (
          !(last(prevEntry[0]?.children) as any).text?.endsWith(' ') &&
          !(first(node.children) as any).text?.startsWith(' ')
        ) {
          const lastText: any = Node.last(window.Editor, prevEntry[1]);
          Transforms.insertText(window.Editor, lastText[0]?.text + ' ', {
            at: lastText[1],
          });
        }

        Transforms.mergeNodes(window.Editor, { at: path });

        const mergedNode: NodeEntry<CustomElement> = Editor.previous(
          window.Editor,
          {
            at: path,
          },
        )!;

        Transforms.setNodes(
          window.Editor,
          {
            data: { ...mergedNode[0].data, end: newEnd },
          } as Partial<Node>,
          { at: mergedNode[1] },
        );
        return mergedNode[0]?.data?.start;
      });
    } catch (error: any) {
      console.log('error :>> ', error);
    } finally {
      console.log(`[perf] merge ${(performance.now() - t0).toFixed(2)}ms`);
    }
  };

  const handleSplitTranscript = () => {
    if (!canSplit()) return;
    try {
      const editor = window?.Editor;
      const sel = editor?.selection;
      if (!sel || !editor) return;

      const parentNode = Editor.parent(editor, Editor.path(editor, sel));
      if (!parentNode) return;

      breakCaption(parentNode[1], false, 'split:tool');
      ReactEditor.deselect(window.Editor);
    } catch (error: any) {
      console.log('split error :>> ', error);
    }
  };

  const handleDeleteTranscript = () => {
    const t0 = performance.now();
    try {
      runStructuralTranscriptEdit(() => {
        const path = ReactEditor.findPath(window.Editor, element);
        const op: RemoveNodeOperation = {
          type: 'remove_node',
          path: path,
          node: element,
        };
        window.Editor.apply(op);
      });
    } finally {
      console.log(`[perf] delete ${(performance.now() - t0).toFixed(2)}ms`);
    }
  };

  const canAdd = () => {
    try {
      const path = ReactEditor.findPath(window.Editor, element);
      const current: any = Node.get(window.Editor, path);
      const next: any = Editor.next(window.Editor, { at: path })?.[0];

      if (isNil(next)) {
        return current?.data?.end < window.Video.duration;
      }

      return current?.data?.end < next?.data?.start;
    } catch (e: any) {
      return false;
    }
  };

  const canMerge = () => {
    try {
      const path = ReactEditor.findPath(window.Editor, element);
      const previous: any = Editor.previous(window.Editor, { at: path })?.[0];
      return !!previous;
    } catch (e: any) {
      return false;
    }
  };

  const canSplit = () => {
    try {
      const editor = window?.Editor;
      const sel = editor?.selection;
      if (!editor || !sel) return false;

      const path = ReactEditor.findPath(editor, element);
      const selPath = sel.anchor.path;
      return Path.equals(selPath.slice(0, path.length), path);
    } catch (e: any) {
      return false;
    }
  };

  const canDelete = () => {
    return window?.Editor?.children?.length > 1;
  };

  const [interactionAllowed, setInteractionAllowed] = useState(() =>
    transcriptChunkStore.isTranscriptInteractionAllowed(),
  );
  const lastGateLogKeyRef = useRef('');

  useLayoutEffect(() => {
    if (!transcriptChunkStore.isEnabled()) return;
    const sync = () => {
      transcriptChunkStore.healStalePlaybackSeekChrome();
      const allowed = transcriptChunkStore.isTranscriptInteractionAllowed();
      const captionStart = element?.data?.start;
      const isActiveCaption = isPlaybackActiveCaption(captionStart);

      if (!allowed && isActiveCaption) {
        const diagnosis = transcriptChunkStore.diagnoseTranscriptLinePlayBlock();
        const gateKey = diagnosis.blockers.join('|');
        if (lastGateLogKeyRef.current !== gateKey) {
          lastGateLogKeyRef.current = gateKey;
          const visibilityKeyLocal = transcriptVisibilityKey(captionStart);
          logTranscriptLinePlayDiagnosis('active-caption-blocked', diagnosis, {
            captionStart,
            isActiveCaption,
            mountHeavy: transcriptVisibility.isHeavyMounted(visibilityKeyLocal),
            pinned: transcriptVisibility.isHeavyMounted(visibilityKeyLocal),
          });
        }
      } else if (allowed && lastGateLogKeyRef.current) {
        lastGateLogKeyRef.current = '';
        logTranscriptLinePlayDiagnosis(
          'active-caption-unblocked',
          transcriptChunkStore.diagnoseTranscriptLinePlayBlock(),
          { captionStart },
        );
      }

      setInteractionAllowed(allowed);
    };
    sync();
    return transcriptChunkStore.subscribe(sync);
  }, [element?.data?.start]);

  const handleClickPlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      transcriptChunkStore.isEnabled() &&
      !transcriptChunkStore.isTranscriptInteractionAllowed()
    ) {
      logTranscriptLinePlayDiagnosis(
        'click-blocked',
        transcriptChunkStore.diagnoseTranscriptLinePlayBlock(),
        {
          captionStart: element?.data?.start,
          mountHeavy: transcriptVisibility.isHeavyMounted(visibilityKey),
        },
        true,
      );
      return;
    }

    const nodeStart = element.data.start;
    const video = window?.Video;
    if (!video) return;

    const captionStart = element?.data?.start;
    if (isPlaybackActiveCaption(captionStart) && !video.paused) {
      video.pause();
      return;
    }

    if (isPlaybackActiveCaption(captionStart) && video.paused) {
      flushPlaybackHighlight(video.currentTime || 0, {
        isFollowing: followTranscript,
        linePlay: true,
      });
      void video.play()?.catch(() => undefined);
      return;
    }

    const seekTime = nodeStart + 0.001;
    startTranscriptLinePlayback(seekTime);
    if (Math.abs(video.currentTime - seekTime) > 0.02) {
      video.currentTime = seekTime;
    }
    try {
      video.dispatchEvent(new Event('timeupdate'));
    } catch {
      // ignore synthetic timeupdate failures
    }
    void video.play()?.catch(() => undefined);
  };

  const [isHover, toggleHover] = useToggle(false);
  const [isDoubleClickSpeaker, toggleDoubleClickSpeaker] = useToggle(false);

  const speakerList = element?.data?.speaker;
  const speakerName = formatSpeaker(
    isArray(speakerList) && !isEmpty(speakerList)
      ? speakerList[0]
      : speakerList,
  );

  const [showDropdown, toggleShowDropdown] = useToggle(false);

  const speakerRef = useRef(null);

  useClickAway(speakerRef, () => {
    toggleDoubleClickSpeaker(false);
    toggleShowDropdown(false);
  });

  const handleMouseEnter = () => {
    if (isReadOnly || isPublicShared() || isHover) return;
    toggleHover(true);
  };

  const handleMouseLeave = () => {
    if (isReadOnly || isPublicShared() || isDoubleClickSpeaker || showDropdown)
      return;
    toggleHover(false);
  };

  const handleDoubleClickTimecode = () => {
    if (isReadOnly) {
      customToast.error('Edit mode is off');
      return;
    }

    onChangeTimecode?.(element);
  };

  const handleShowDropdown = () => {
    if (showDropdown) return;
    toggleShowDropdown(true);
  };

  const handleClickAddAction = () => {
    handleAddTranscript(element);
    update();
  };

  const showHeavy =
    mountHeavy || transcriptVisibility.isHeavyMounted(visibilityKey);

  return (
    <div
      ref={shellRef}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...attributes}
      data-start={`${element?.data?.start}`}
      data-end={`${element?.data?.end}`}
      tw="my-6 border-bottom[1px solid #e5e5e5] flex"
      className="video_mgn"
      css={isPublicShared() && highlightNodeHoverCss}
      title={
        isDevMode() &&
        `[${ReactEditor.findPath(window.Editor, element)}] | ${
          element?.data?.start
        } - ${element?.data?.end}`
      }
    >
      <div
        tw="lg-up:(flex) w-full"
        className="editInpt"
        css={[isPublicShared() && tw`cursor-pointer`]}
      >
        <div
          className="left_blcok"
          contentEditable={false}
          style={{ userSelect: 'none' }}
          tw="mt-0 flex"
        >
          <Hint open={false} text="Section start time">
            <div
              className="content_block"
              tw="lg-up:(min-width[16rem]) md-down:(flex space-x-10)"
            >
              <span
                tw="cursor-pointer whitespace-nowrap"
                css={[!isReadOnly && timecodeEditable]}
                title="Double click to edit"
                onDoubleClick={handleDoubleClickTimecode}
              >
                {secToTimeCode(element.data.start)}
              </span>
              <div
                ref={speakerRef}
                onClick={handleShowDropdown}
                tw="flex w-full justify-between items-center cursor-pointer py-0.5 rounded"
              >
                <h6
                  tw="text-17 font-medium my-0! opacity-80 lg-up:max-width[13rem]"
                  className="line-clamp-3"
                >
                  {speakerName}
                </h6>

                {!isReadOnly && !showDropdown && (
                  <div
                    tw="flex cursor-pointer hover:filter[drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.3))] pl-4"
                    css={!isHover && tw`invisible`}
                  >
                    <ArrowDownSvg />
                  </div>
                )}

                {isHover && !isReadOnly && showDropdown && (
                  <SpeakerDropdown
                    showDropdown={showDropdown}
                    toggleShowDropdown={toggleShowDropdown}
                    toggleDoubleClickSpeaker={toggleDoubleClickSpeaker}
                    node={element}
                    tw="lg-down:(w-1/2)"
                  />
                )}
              </div>
            </div>
          </Hint>
        </div>

        <span tw="md-up:flex w-full">
          <div
            className="left_blcok"
            tw="sm-down:(hidden)! flex items-center"
            style={{ userSelect: 'none' }}
          >
            <div className="btn_block">
              <button
                type="button"
                className="button"
                tabIndex={-1}
                disabled={!interactionAllowed}
                aria-disabled={!interactionAllowed}
                css={[
                  transcriptButtonCss,
                  !interactionAllowed &&
                    tw`cursor-not-allowed opacity-50 pointer-events-none`,
                ]}
                onClick={handleClickPlay}
              >
                <Play className="playBackPlay" />
                <Tplay className="playBackPause" />
              </button>
            </div>
          </div>

          <div
            tw="flex-1 md:(mt-2)"
            // PS1: browser skips layout/paint for off-screen rows; pin/IO keeps playback/search rows hot
            css={
              !showHeavy
                ? {
                    contentVisibility: 'auto',
                    containIntrinsicSize: 'auto 3.5rem',
                  }
                : undefined
            }
          >
            {children}
          </div>
        </span>
      </div>

      {/* Fixed-width slot; icons are absolutely positioned so hover never resizes the row */}
      <div
        tw="mx-2 relative min-width[2.5rem] self-stretch"
        contentEditable={false}
        style={{ userSelect: 'none' }}
      >
        {isHover && !isReadOnly && (
          <div tw="absolute right-0 top-1/2 flex flex-col justify-around min-width[2.5rem] z-10 transform[translateY(-50%)]">
            <Hint text="Add a new caption in the gap after this caption">
              <Ripple
                onClick={handleClickAddAction}
                className={canAdd() ? '' : 'disabled'}
              >
                <div css={iconStyles}>
                  <AddSvg />
                </div>
              </Ripple>
            </Hint>
            <Hint text="Merge with previous caption">
              <Ripple
                onClick={handleMergeTranscript}
                className={canMerge() ? '' : 'disabled'}
              >
                <div css={iconStyles}>
                  <MergeSvg />
                </div>
              </Ripple>
            </Hint>
            <Hint text="Split caption at caret">
              <Ripple
                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                onClick={handleSplitTranscript}
                className={canSplit() ? '' : 'disabled'}
              >
                <div css={iconStyles}>
                  <SplitSvg />
                </div>
              </Ripple>
            </Hint>
            <Hint text="Delete caption">
              <Ripple
                onClick={handleDeleteTranscript}
                className={canDelete() ? '' : 'disabled'}
              >
                <div css={iconStyles}>
                  <DeleteSvg />
                </div>
              </Ripple>
            </Hint>
          </div>
        )}
      </div>
    </div>
  );
});
